import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { WidenService } from '@services/widen.service';
import {
  ArticleHubContent,
  RelatedStrategies,
  InsightsStickyTab,
  StrategiesTab,
} from '@marketing/insights/insights.interface';
import { Observable, Subject } from 'rxjs';
import { ResponsiveService } from '@frk/eds-components';
import { LocalLinkService } from '@services/local-link.service';
import { Page, Component as BrComponent } from '@bloomreach/spa-sdk/';
import { LinkService } from '@services/link.service';
import {
  AppStateService,
  SegmentService,
  SiteConfigService,
  TabsStickyService,
  WindowScrollService,
} from '@services';
import { ArticleService } from '@marketing/article/services/article.service';
import { takeUntil } from 'rxjs/operators';
import { StrategyTabsService } from '@services/strategy-tabs.service';
import { StickyTab, StickyTabChange } from '@frk/eds-components';
import { Logger } from '@utils/logger';

const logger = Logger.getLogger('ArticleHubComponent');

/**
 * The Article Hub component can be used to display hub page for article
 *
 * Selector: `ft-article-hub`
 *
 * Exported as: `ArticleHubComponent`
 */
@Component({
  selector: 'ft-article-hub',
  templateUrl: './article-hub.component.html',
  styleUrls: ['article-hub.component.scss'],
})
export class ArticleHubComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy {
  public page: Page;
  public articleHubInfo: ArticleHubContent;
  translationLabels: any;
  isMobile$: Observable<boolean>;
  public relatedStrategies: RelatedStrategies;
  private currentInvestmentTeam: string;

  /**
   * SIM logo resource bundle object
   */
  simLogoResourceBundle: object;
  segmentTags: string;
  /**
   * used to remove subscriptions when component is destroyed
   */
  private unsubscribe$: Subject<void> = new Subject<void>();

  public stickyTabs: StickyTab[];

  public activeTabId: string;

  /**
   * Constructor
   */
  constructor(
    private widenService: WidenService,
    private localLinkService: LocalLinkService,
    private siteConfig: SiteConfigService,
    private linkService: LinkService,
    private appStateService: AppStateService,
    private segmentService: SegmentService,
    private articleService: ArticleService,
    private responsiveService: ResponsiveService,
    private strategyTabsService: StrategyTabsService,
    protected changeDetectorRef: ChangeDetectorRef,
    private tabsStickyService: TabsStickyService,
    private scrollService: WindowScrollService
  ) {
    super();
    this.isMobile$ = responsiveService.isMobile$();
    this.segmentService
      .getCurrentSegment$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((segment) => {
        this.segmentTags = segment?.insightAudiences;
      });
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.setBreadcrumbItems();
    this.translationLabels = this.component?.getModels()[
      'ft.core.insightsLibrary'
    ];
    this.simLogoResourceBundle = this.component?.getModels()[
      'ft.core.sim-logos'
    ];
    this.articleHubInfo = this.document?.getData<ArticleHubContent>();

    this.currentInvestmentTeam = this.articleHubInfo?.articleFilters?.investmentteam;

    this.articleHubInfo.summary.value = this.localLinkService.getLocalizeLinks(
      this.articleHubInfo.summary.value
    );
    if (this.articleHubInfo?.logo) {
      this.articleHubInfo.imageSrc = this.widenService.getWidenImageVariantUrl(
        this.widenService.getWidenAssetUrl(
          this.articleHubInfo.logo.widenDocument
        ),
        'original',
        'webp'
      );
    }

    // Sticky Nav
    if (this.articleHubInfo?.stickyNav) {
      this.stickyTabs = this.strategyTabsService.getStickyNavigation(
        this.articleHubInfo.stickyNav,
        this.segmentTags
      );
    }

    // Related Strategies
    if (this.articleHubInfo?.strategySectionCompound) {
      this.relatedStrategies = this.strategyTabsService.createStrategyTabsSection(
        this.articleHubInfo?.strategySectionCompound,
        this.segmentTags,
        this.page
      );
    }
    // Set section nav in service

    this.tabsStickyService.setStickyTabs(this.stickyTabs);

    // Listen for active tab change and update highlight

    this.tabsStickyService
      .getActiveTabId$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((id: string): void => {
        logger.debug('getActiveTabId$', id);
        this.activeTabId = id;
        this.changeDetectorRef.detectChanges();
      });
  }

  // called when sticky tab clicked
  public onTabChange(tabEvent: StickyTabChange): void {
    logger.debug('onTabChange()', tabEvent);
    if (tabEvent.type !== 'manual') {
      // this will suppress ScrollSpy for non-scroll events
      this.tabsStickyService.tabClicked(tabEvent.tab);
    }
  }

  /**
   * sticky tabs height has changed
   * @param id - tab id
   */
  public onHeightChange(height: number): void {
    logger.debug('onHeightChange()', height);
    // this will update the scroll offset position based on tabs height
    this.scrollService.stickyTabsResize('article-hub-sticky-tabs', height);
  }

  /**
   * On component destroyed
   * Removes any remaining subscriptions
   */
  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
